import { EdgesGeometry, LessEqualDepth, LineBasicMaterial, LineSegments, Mesh } from 'three';

/** Edge colour: a dark wood shade, so a face edge reads as a joint, not as an outline. */
// native-ok: WebGL takes a colour value, not a CSS variable
export const EDGE_LINE_COLOR: number = 0x2a1c12;

/** Edge opacity: the line darkens the edge without cutting the board out like a drawing. */
export const EDGE_LINE_OPACITY: number = 0.55;

/**
 * Crease angle in degrees. Box corners (90°) and the kosour teeth get a line; the 48-facet
 * pole (7.5° between facets) does not.
 */
export const EDGE_THRESHOLD_DEG: number = 30;

/** Name suffix of an edge child: `tread-0` carries `tread-0-edges`. */
export const EDGE_NAME_SUFFIX: string = '-edges';

/**
 * The shared material of all edge lines. WebGL applies `polygonOffset` to polygons only, so a
 * line wins over the face it lies on by drawing after it (`renderOrder: 1`, transparent) with
 * `LessEqualDepth` — see the depth-fight table in `.claude/rules/three-js.md`.
 */
export function createEdgeMaterial(): LineBasicMaterial {
    return new LineBasicMaterial({
        color: EDGE_LINE_COLOR,
        transparent: true,
        opacity: EDGE_LINE_OPACITY,
        depthFunc: LessEqualDepth,
        depthWrite: false,
    });
}

/**
 * Adds edge lines to a mesh as its child, so they follow its position and rotation. The edge
 * geometry belongs to the child and is freed with the mesh by the scene's group disposal; the
 * material is shared and is freed by its owner.
 */
export function addEdgeLines(mesh: Mesh, material: LineBasicMaterial): Mesh {
    // An empty placeholder mesh (a stringer with fewer than two points) has no faces to outline.
    if (!('position' in mesh.geometry.attributes)) {
        return mesh;
    }
    const edges: LineSegments = new LineSegments(new EdgesGeometry(mesh.geometry, EDGE_THRESHOLD_DEG), material);
    edges.name = `${mesh.name}${EDGE_NAME_SUFFIX}`;
    edges.renderOrder = 1;
    mesh.add(edges);
    return mesh;
}
